import { createStore, createEvent, forward, createEffect, sample } from 'effector';
import { EditorInstance } from '@nx-frontend/shared/types';

/* Global access to editor instance and the queue of functions to be executed when editor is initialized */

// Event for updating editor instance in the store
export const setWebEditorEvent = createEvent<EditorInstance | null>();
// Event for adding functions to the queue. The queue will fire the next time editor is initialized
export const addWebEditorOnInitQueueEvent = createEvent<(editor: EditorInstance) => void>();

export const $webEditor = createStore<EditorInstance | null>(null);
export const $addWebEditorOnInitQueue = createStore<((editor: EditorInstance) => void)[]>([]);

forward({
  from: setWebEditorEvent,
  to: $webEditor,
});

$addWebEditorOnInitQueue.on(addWebEditorOnInitQueueEvent, (queue, fn) => [...queue, fn]);

// When updating $webEditor store, if editor is not null, the queued functions will be called
$addWebEditorOnInitQueue.on($webEditor, (queue, editor) => {
  if (editor !== null) {
    if (queue.length) {
      for (const fn of queue) {
        fn(editor);
      }
      return [];
    }
  }
  return queue;
});

/**
 * Store for storing all the editors on page
 * In portal page we have multiple editors, so we need to store them all
 */

export interface WebEditors {
  page: string | null;
  editor: EditorInstance[];
}

const webEditorsInitialState: WebEditors = {
  page: null,
  editor: [],
};

export const $webEditors = createStore<WebEditors>(webEditorsInitialState);

interface SetWebEditorsEventPayload {
  page: string;
  editor: EditorInstance;
  blockId?: string;
}

export const setWebEditorsEvent = createEvent<SetWebEditorsEventPayload>();

$webEditors.on(setWebEditorsEvent, (state, { page, editor, blockId }) => {
  let items: EditorInstance[] = state.editor;
  if (state.page !== page) {
    items = [];
  }
  editor.blockId = blockId;
  items = items.filter((item) => {
    // keep in array unique editors
    if (item.blockId !== blockId) {
      return true;
    }
    // in case if in editors array has editor with the same blockId,
    // need to destroy it and remove from array
    item.destroy();
    return false;
  });
  items.push(editor);
  return {
    page,
    editor: items,
  };
});

export const updateWebEditorsScaleEvent = createEvent<number>();

const updateWebEditorsScaleFx = createEffect(
  async ({ webEditors, scale }: { webEditors: WebEditors; scale: number }) => {
    webEditors.editor.forEach((editor) => {
      editor.options.scale = scale;
      const commentManager = editor.plugins.commentManager;
      if (commentManager) {
        commentManager.onEditorUpdate();
        commentManager.options.getPluginInstance().disableOpenComments = editor.options.disableOpenComments;
      }
    });
  }
);

sample({
  source: { webEditors: $webEditors },
  clock: updateWebEditorsScaleEvent,
  fn: (store, scale) => {
    return { webEditors: store.webEditors, scale };
  },
  target: updateWebEditorsScaleFx,
});
